import React from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogActions
} from "@mui/material";
import Heading from '../../../WebApp/Components/Heading/heading.tsx';
interface Vehicle {
  fuel_type: string;
}

interface CurrentTrip {
  odometer_start?: string;
  orders_picked?: string;
  start_time?: string | null;
  end_time?: string | null;
  status?: string;
  initiated_by?: string;
}

interface TripFormProps {
  open: boolean;
  onClose: () => void;
  onNext: () => void;
  onCancel: () => void;
  actionType: 'start' | 'end';
  selectedVehicle: Vehicle | null;
  odometerReading: string;
  onOdometerChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  ordersPicked: string;
  onOrdersPickedChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  ordersDelivered: string;
  onOrdersDeliveredChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  currentTrip: CurrentTrip;
  handoverStatus: boolean;
}

const TripForm: React.FC<TripFormProps> = ({
  open,
  onClose,
  onNext,
  onCancel,
  actionType,
  selectedVehicle,
  odometerReading,
  onOdometerChange,
  ordersPicked,
  onOrdersPickedChange,
  ordersDelivered,
  onOrdersDeliveredChange,
  currentTrip,
  handoverStatus
}) => {
  const is2WEV = selectedVehicle?.fuel_type === '2W-EV';
  
  const getFormTitle = (): string => {
    if (actionType === 'start') return 'Start Trip';
    if (currentTrip.status === 'handover') return 'Handover Trip';
    return 'End Trip';
  };

  // Validation helpers
  const getMaxOrdersDelivered = (): number => {
    return parseInt(currentTrip.orders_picked || ordersPicked || '0');
  };

  const isOrdersDeliveredValid = (): boolean => {
    const delivered = parseInt(ordersDelivered) || 0;
    const maxAllowed = getMaxOrdersDelivered();
    return delivered <= maxAllowed;
  };

  const isOdometerValid = (): boolean => {
    if (actionType === 'end' && currentTrip.odometer_start) {
      return parseInt(odometerReading) > parseInt(currentTrip.odometer_start);
    }
    return !!odometerReading;
  };



  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 1
        }
      }}
    >
      <Heading 
        title={getFormTitle()}
        closeIcon="true"
        onClose={onClose}
      />
    
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Odometer Reading"
            type="number"
            value={odometerReading}
            onChange={onOdometerChange}
            fullWidth
            variant="outlined"
            size="small"
            InputProps={{
              inputProps: { 
                min: actionType === 'end' && currentTrip.odometer_start ? 
                  parseInt(currentTrip.odometer_start) + 1 : 0 
              }
            }}
            error={actionType === 'end' && currentTrip.odometer_start && 
                   parseInt(odometerReading) <= parseInt(currentTrip.odometer_start)}
            helperText={
              actionType === 'end' && currentTrip.odometer_start ? 
              `Must be greater than start reading: ${currentTrip.odometer_start}` : ''
            }
          />
          
          {selectedVehicle?.fuel_type === "2W-EV" ? (
            actionType === "end" && (
              <>
                <TextField
                  label="Orders Picked"
                  type="number"
                  value={ordersPicked}
                  onChange={onOrdersPickedChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
                <TextField
                  label="Orders Delivered"
                  type="number"
                  value={ordersDelivered}
                  onChange={onOrdersDeliveredChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    inputProps: { 
                      min: 0,
                      max: getMaxOrdersDelivered()
                    }
                  }}
                  helperText={
                    !isOrdersDeliveredValid() 
                      ? `Delivered cannot exceed picked (${getMaxOrdersDelivered()})` 
                      : ""
                  }
                  error={!isOrdersDeliveredValid()}
                />
              </>
            )
          ) : (
            <>
              {actionType === "start" && (
                <TextField
                  label="Orders Picked"
                  type="number"
                  value={ordersPicked}
                  onChange={onOrdersPickedChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              )}
              {actionType === "end" && (
                <TextField
                  label="Orders Delivered"
                  type="number"
                  value={ordersDelivered}
                  onChange={onOrdersDeliveredChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    inputProps: { 
                      min: 0,
                      max: getMaxOrdersDelivered()
                    }
                  }}
                  helperText={
                    !isOrdersDeliveredValid()
                      ? `Delivered cannot exceed picked (${getMaxOrdersDelivered()})` 
                      : ""
                  }
                  error={!isOrdersDeliveredValid()}
                />
              )}
            </>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ padding: 2, gap: 1 }}>
        <Button 
          variant="outlined" 
          onClick={onCancel}
          sx={{ 
            borderColor: 'error.main', 
            color: 'error.main',
            flex: 1
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={onNext}
          sx={{ 
            backgroundColor: 'success.main',
            flex: 1
          }}
        >
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TripForm;